import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Heart, Mail, Lock } from "lucide-react";
import { api, setToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import FeedbackFooter from "@/components/FeedbackFooter";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { setUser } = useAuth();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await api.post("/auth/login", { email, password });
            setToken(data.token);
            setUser(data.user);
            toast.success(`Welcome back, ${data.user.name}`);
            navigate("/app", { replace: true });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Sign in failed");
        } finally {
            setLoading(false);
        }
    };

    const handleGoogle = () => {
        const redirectUrl = window.location.origin + "/app";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <div className="min-h-screen relative overflow-hidden bg-background flex items-center justify-center p-6">
            <div className="blob blob-peach w-[500px] h-[500px] -top-40 -left-40" />
            <div className="blob blob-cream w-[500px] h-[500px] -bottom-40 -right-40" />

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: "easeOut" }}
                className="relative z-10 w-full max-w-md"
            >
                <Link to="/" className="flex items-center gap-2 mb-10" data-testid="login-brand">
                    <div className="w-10 h-10 rounded-2xl bg-primary flex items-center justify-center shadow-soft">
                        <Heart className="w-5 h-5 text-primary-foreground" strokeWidth={2} fill="currentColor" />
                    </div>
                    <span className="font-heading text-2xl font-[800] tracking-tight">CheerUp!</span>
                </Link>

                <div className="bg-card rounded-3xl p-8 sm:p-10 shadow-soft-lg border border-border">
                    <h1 className="font-heading text-3xl font-[800] tracking-tight mb-2">Welcome back.</h1>
                    <p className="text-muted-foreground mb-8">We're glad you're here.</p>

                    <Button
                        type="button"
                        variant="outline"
                        className="w-full h-12 rounded-2xl mb-6 bg-white hover:bg-accent/50 border-border font-medium"
                        onClick={handleGoogle}
                        data-testid="login-google-btn"
                    >
                        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Continue with Google
                    </Button>

                    <div className="flex items-center gap-3 mb-6">
                        <div className="h-px flex-1 bg-border" />
                        <span className="text-xs uppercase tracking-widest text-muted-foreground">or</span>
                        <div className="h-px flex-1 bg-border" />
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div>
                            <Label htmlFor="email" className="text-sm font-medium mb-2 block">Email</Label>
                            <div className="relative">
                                <Mail className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5} />
                                <Input
                                    id="email"
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    className="h-12 pl-11 rounded-2xl bg-background border-border"
                                    data-testid="login-email-input"
                                />
                            </div>
                        </div>
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                                <Link to="/forgot-password" className="text-xs text-primary font-medium hover:underline" data-testid="login-forgot-password-link">
                                    Forgot password?
                                </Link>
                            </div>
                            <div className="relative">
                                <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5} />
                                <Input
                                    id="password"
                                    type="password"
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    className="h-12 pl-11 rounded-2xl bg-background border-border"
                                    data-testid="login-password-input"
                                />
                            </div>
                        </div>
                        <Button
                            type="submit"
                            disabled={loading}
                            className="w-full h-12 rounded-2xl font-medium shadow-soft"
                            data-testid="login-submit-btn"
                        >
                            {loading ? "Signing in…" : "Sign in"}
                        </Button>
                    </form>

                    <p className="text-center text-sm text-muted-foreground mt-6">
                        New here?{" "}
                        <Link to="/signup" className="text-primary font-medium hover:underline" data-testid="login-signup-link">
                            Create an account
                        </Link>
                    </p>
                    <p className="text-center text-xs text-muted-foreground mt-4">
                        By signing in you agree to our{" "}
                        <Link to="/terms" className="text-primary underline" data-testid="login-terms-link">
                            Terms
                        </Link>
                        {" "}and{" "}
                        <Link to="/privacy" className="text-primary underline" data-testid="login-privacy-link">
                            Privacy Policy
                        </Link>
                        .
                    </p>
                </div>
                <FeedbackFooter />
            </motion.div>
        </div>
    );
}
